/**
 * hover-scroll.js
 *
 * Carrossel "edge-hover" fluido com loop infinito, para containers
 * marcados com [data-hover-scroll].
 *
 * - Parado por padrão. Sem setas.
 * - Mouse próximo da borda ESQUERDA  → rola para a esquerda.
 * - Mouse próximo da borda DIREITA   → rola para a direita.
 * - Velocidade proporcional à proximidade da borda, com suavização
 *   (easing) para não ficar "travado".
 * - Loop infinito: o conteúdo é renderizado em 3 cópias (ver
 *   Universos.tsx) e fazemos um "wrap" invisível na cópia do meio.
 * - Convive com drag-scroll.js: enquanto o usuário arrasta
 *   ([is-dragging]) o auto-scroll pausa e o wrap fica suspenso.
 * - Touch/mobile: mousemove não dispara, então sobra o swipe nativo.
 *
 * PERF: antes um requestAnimationFrame rodava para sempre, em todo device,
 * lendo el.scrollLeft duas vezes por frame — um forced reflow a cada 16ms
 * que aparecia como "Reflow forçado" no PageSpeed e comia TBT. No mobile
 * era 100% desperdício (mousemove nunca dispara). Agora:
 *   - o rAF só existe em ponteiro fino com hover (desktop), e só enquanto
 *     há velocidade para aplicar; parado, ele desliga;
 *   - o wrap infinito saiu do rAF e virou um listener de 'scroll' passivo,
 *     que roda só quando o carrossel realmente se move (inclusive no swipe
 *     nativo do mobile, onde o loop continua funcionando).
 *
 * Vanilla JS de propósito (portável para um tema Shopify via asset_url).
 */
(function () {
  var EDGE_ZONE = 0.30; // faixa sensível em cada borda (30% da largura)
  var MAX_SPEED = 14;   // px/frame no canto mais extremo
  var EASING = 0.09;    // suavização: menor = mais suave/fluido

  // Só faz sentido em quem tem mouse de verdade. Em touch o rAF nunca teria
  // trabalho útil, porque mousemove não dispara.
  var CAN_HOVER =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  function init(el) {
    var target = 0;  // velocidade desejada (definida pelo mouse)
    var speed = 0;   // velocidade atual (interpolada → fluidez)
    var unit = 0;    // largura de UMA cópia do conjunto (passo do loop)
    var started = false;
    var running = false; // há um rAF agendado?

    var count = parseInt(el.dataset.loopCount || "0", 10);

    // Mede a largura de uma cópia usando a distância entre o primeiro
    // card da 1ª cópia e o primeiro card da 2ª cópia (exato, inclui o gap).
    function measure() {
      var items = el.children;
      if (count > 0 && items.length >= count + 1) {
        unit = items[count].offsetLeft - items[0].offsetLeft;
      } else {
        unit = el.scrollWidth / 3;
      }
      // Posiciona no início da cópia do meio (só na primeira medição,
      // antes de qualquer interação) — há cópia à esquerda e à direita,
      // então dá para rolar nos dois sentidos sem bater no limite.
      if (!started && unit > 0) {
        el.scrollLeft = unit;
        started = true;
      }
    }

    // Wrap invisível: mantém o scroll na cópia do meio [unit, 2*unit).
    // Roda no evento de scroll (a posição já está calculada aqui, então ler
    // scrollLeft não força layout), e não durante o arraste, para não brigar
    // com o drag-scroll.
    function wrap() {
      if (unit <= 0 || el.classList.contains("is-dragging")) return;
      var x = el.scrollLeft;
      if (x >= unit * 2) el.scrollLeft = x - unit;
      else if (x < unit) el.scrollLeft = x + unit;
    }

    function onMove(e) {
      var rect = el.getBoundingClientRect();
      var ratio = (e.clientX - rect.left) / rect.width;
      if (ratio < EDGE_ZONE) {
        target = -MAX_SPEED * (1 - ratio / EDGE_ZONE);
      } else if (ratio > 1 - EDGE_ZONE) {
        target = MAX_SPEED * ((ratio - (1 - EDGE_ZONE)) / EDGE_ZONE);
      } else {
        target = 0;
      }
      start();
    }

    function onLeave() {
      target = 0;
      start(); // deixa a velocidade decair suavemente até parar
    }

    function start() {
      if (running) return;
      running = true;
      requestAnimationFrame(loop);
    }

    function loop() {
      var dragging = el.classList.contains("is-dragging");

      // Suaviza a velocidade até o alvo (0 quando arrastando).
      var t = dragging ? 0 : target;
      speed += (t - speed) * EASING;
      if (Math.abs(speed) < 0.03) speed = 0;
      if (!dragging && speed !== 0) el.scrollLeft += speed;

      // Nada para fazer e nada pedido: desliga o rAF em vez de girar à toa.
      if (speed === 0 && t === 0) {
        running = false;
        return;
      }
      requestAnimationFrame(loop);
    }

    var resizeTimer;
    window.addEventListener("resize", function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(measure, 150);
    });
    window.addEventListener("load", measure);

    // O wrap vale para todo mundo: é ele que mantém o loop infinito também
    // no swipe nativo do mobile.
    el.addEventListener("scroll", wrap, { passive: true });

    if (CAN_HOVER) {
      el.addEventListener("mousemove", onMove);
      el.addEventListener("mouseleave", onLeave);
    }

    measure();
  }

  function initAll() {
    document.querySelectorAll("[data-hover-scroll]").forEach(init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }
})();
